// src/axiosInstance.js
import axios from "axios";

// set a baseURL for API calls (adjust if your API is hosted elsewhere)
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

const axiosInstance = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Optional: attach token to every request if present
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axiosInstance.interceptors.response.use(
  response => response,
  async (error) => {
    const originalRequest = error?.config;

    // If unauthorized and we haven't already retried this request
    if (error?.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        const res = await axios.post(`${API_BASE}/logout`).catch(() => {});
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        // Use plain axios to avoid interceptors loop
        const res = await axios.post(`${API_BASE}/refresh`, { refresh_token: refreshToken });

        const newAccessToken = res.data.token || res.data.access_token;
        const newRefreshToken = res.data.refresh_token;

        if (!newAccessToken) {
          throw new Error('No access token returned from refresh endpoint');
        }

        // store new tokens
        localStorage.setItem('token', newAccessToken);
        if (newRefreshToken) {
          localStorage.setItem('refresh_token', newRefreshToken);
        }

        // update axiosInstance default header and original request header
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;

        // retry original request
        return axiosInstance(originalRequest);
      } catch (refreshErr) {
        console.error('Refresh token invalid or expired:', refreshErr);
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshErr);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
